 
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { ExternalLink, RefreshCw } from 'lucide-react'
import { supabase } from '../integrations/supabase/client'

interface PaymentsTableProps {
  userId: string
}

export function PaymentsTable({ userId }: PaymentsTableProps) {
  const { data: payments, isLoading, refetch } = useQuery({
    queryKey: ['payments', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('asaas_payments')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(20)
      
      if (error) throw error
      return data
    },
    enabled: !!userId,
  })

  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    confirmed: 'bg-blue-500/20 text-blue-400',
    received: 'bg-emerald-500/20 text-emerald-400',
    overdue: 'bg-red-500/20 text-red-400',
    refunded: 'bg-slate-500/20 text-slate-400',
  }

  const statusLabels: Record<string, string> = {
    pending: 'Pendente',
    confirmed: 'Confirmado',
    received: 'Recebido',
    overdue: 'Vencido',
    refunded: 'Reembolsado',
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!payments || payments.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <p className="text-sm text-slate-400">Nenhum pagamento encontrado</p>
          <button
            onClick={() => refetch()}
            className="px-3 py-1.5 text-sm border border-white/10 rounded-lg text-slate-300 hover:bg-white/5 transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </button>
        </div>
        <div className="text-center py-8 text-slate-400">
          <p>Pagamentos via ASAAS aparecerão aqui</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-400">
          {payments.length} pagamento(s) encontrado(s)
        </p>
        <button
          onClick={() => refetch()}
          className="px-3 py-1.5 text-sm border border-white/10 rounded-lg text-slate-300 hover:bg-white/5 transition-colors flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase tracking-wider">
                Data
              </th>
              <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase tracking-wider">
                Valor
              </th>
              <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase tracking-wider">
                Status
              </th>
              <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase tracking-wider">
                Vencimento
              </th>
              <th className="text-left py-3 px-4 text-xs font-medium text-slate-400 uppercase tracking-wider">
                Pagamento
              </th>
              <th className="text-right py-3 px-4 text-xs font-medium text-slate-400 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
        <tbody className="divide-y divide-white/5">
          {payments.map((payment) => (
            <tr key={payment.id} className="hover:bg-white/5 transition-colors">
              <td className="py-4 px-4 text-sm text-slate-300">
                {new Date(payment.created_at).toLocaleDateString('pt-BR')}
              </td>
              <td className="py-4 px-4 text-sm font-medium text-white">
                R$ {payment.value?.toFixed(2) || '0.00'}
              </td>
              <td className="py-4 px-4">
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[payment.status] || 'bg-slate-500/20 text-slate-400'}`}>
                  {statusLabels[payment.status] || payment.status}
                </span>
              </td>
              <td className="py-4 px-4 text-sm text-slate-400">
                {payment.due_date ? new Date(payment.due_date).toLocaleDateString('pt-BR') : '-'}
              </td>
              <td className="py-4 px-4 text-sm text-slate-400">
                {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString('pt-BR') : '-'}
              </td>
              <td className="py-4 px-4 text-right">
                {payment.invoice_url && (
                  <button
                    onClick={() => window.open(payment.invoice_url, '_blank')}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    title="Abrir fatura"
                  >
                    <ExternalLink className="w-4 h-4 text-slate-400" />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </div>
  )
}
